import React, { useEffect, useState } from "react";
import { FaFileAlt, FaCalendarAlt, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import UserDashboard from "./UserDashboard";
import taskAPI from "../api/taskAPI";

const WorkerReportsView = () => {

  const navigate = useNavigate();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedImage, setSelectedImage] = useState(null);

  // 🔎 Filters
  const [searchAssignee, setSearchAssignee] = useState("");
  const [filterDate, setFilterDate] = useState("");

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await taskAPI.get("/task-reports");
      setReports(res.data.reports);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-GB");
  };

  // 🔥 Filter Logic
  const filteredReports = reports.filter((report) => {

    const matchAssignee = report.assigneeId
      .toLowerCase()
      .includes(searchAssignee.toLowerCase());

    const matchDate = filterDate
      ? new Date(report.createdAt).toISOString().slice(0,10) === filterDate
      : true;

    return matchAssignee && matchDate;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">

      <UserDashboard />

      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <FaFileAlt className="text-green-600" />
            Worker Reports
          </h1>
          <p className="text-gray-500 mt-1">
            All submitted reports from workers
          </p>
        </div>

        <button
          onClick={() => navigate("/company-workers")}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition"
        >
          ← Back
        </button>
      </div>

      {/* 🔎 Filters */}
      <div className="flex flex-wrap gap-4 mb-6 bg-white p-4 rounded-xl shadow border">

        <input
          type="text"
          placeholder="Search by Assignee ID..."
          value={searchAssignee}
          onChange={(e) => setSearchAssignee(e.target.value)}
          className="border px-4 py-2 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />

        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
        />

        <button
          onClick={() => {
            setSearchAssignee("");
            setFilterDate("");
          }}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          Clear
        </button>

      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border">

        <table className="min-w-full text-sm text-gray-700">

          <thead className="bg-gray-100 text-gray-600 uppercase text-xs sticky top-0">
            <tr>
              <th className="px-6 py-4">Date</th>
              <th className="px-6 py-4">Assignee</th>
              <th className="px-6 py-4">Task</th>
              <th className="px-6 py-4">Description</th>
              <th className="px-6 py-4">Photo</th>
            </tr>
          </thead>

          <tbody className="divide-y">

            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-6">
                  Loading...
                </td>
              </tr>
            ) : filteredReports.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-6 text-gray-400">
                  No reports found
                </td>
              </tr>
            ) : (
              filteredReports.map((report) => (
                <tr key={report._id} className="hover:bg-gray-50 transition">

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="text-blue-500" />
                      {formatDate(report.createdAt)}
                    </div>
                  </td>

                  <td className="px-6 py-4 font-medium text-indigo-600 flex items-center gap-2">
                    <FaUser />
                    {report.assigneeId}
                  </td>

                  <td className="px-6 py-4 font-semibold">
                    {report.taskTitle}
                  </td>

                  <td className="px-6 py-4 text-gray-600">
                    {report.description}
                  </td>

                  <td className="px-6 py-4">
                    {report.photo && (
                      <img
                        src={report.photo}
                        alt="report"
                        onClick={() => setSelectedImage(report.photo)}
                        className="w-20 h-20 object-cover rounded-lg cursor-pointer border hover:scale-105 transition"
                      />
                    )}
                  </td>

                </tr>
              ))
            )}

          </tbody>

        </table>
      </div>

      {/* 🔥 Image Popup */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-3xl w-full p-4">
            <img
              src={selectedImage}
              alt="Large View"
              className="w-full rounded-xl shadow-2xl border-4 border-white"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 bg-white px-3 py-1 rounded-full shadow hover:bg-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default WorkerReportsView;